import { Box, Text, Stack, HStack, VStack, Image } from '@chakra-ui/react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { ReactElement, useState } from 'react';

import WrappedText from '@/components/wrappedText';
import { Menu } from '@/graphqlTypes';
import ApolloClientManager from '@/libs/apolloClient/apolloClientManager';
import { format } from '@/libs/dateFormat';
import { liffSsrStateRedirect } from '@/libs/liffSupport';
import numberFormat from '@/libs/number';
import { LayoutNextPage } from '@/types';
import CustomButton from '@/userTool/components/common/customButton';
import {
  GetReservationPageIndexPageDocument,
  GetReservationPageIndexPageQuery,
  GetReservationPageIndexPageQueryVariables,
} from '@/userTool/graphql/pages/reservationPage/index.generated';
import {
  GetReservationPageCommonDocument,
  GetReservationPageCommonQuery,
  GetReservationPageCommonQueryVariables,
} from '@/userTool/graphql/reservationPageCommon.generated';
import UserPageLayout from '@/userTool/layouts/userPageLayout';
import styles from '@/userTool/styles/listGroup.module.scss';

export interface ServerProps {
  result: GetReservationPageIndexPageQuery;
  rvPage: GetReservationPageCommonQuery;
}

const MenuComponent: React.VFC<{
  menu: Menu;
  props: ServerProps;
}> = function MenuComponent({ menu, props }) {
  const router = useRouter();

  const { rvPage } = props;
  const [isShowShortDescription, setIsShowShortDescription] = useState<boolean>(true);
  const maxLength = 88;
  const isCutDescription = () => {
    return menu.description && menu.description.length > maxLength;
  };
  const handleToggleShortDescription = () => {
    setIsShowShortDescription(false);
  };

  const handleNextLink = (menuId: number) => {
    return rvPage.reservationPage?.reservation_type === 1 &&
      rvPage.reservationPage?.is_staff_selectable
      ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${rvPage.reservationPage.rv_page_code}/menu/${menuId}/staff`
      : // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${rvPage.reservationPage?.rv_page_code}/calendar/?menuId=${menuId}&date=${encodeURI(
          format(new Date(), 'yyyy-MM-dd')
        )}`;
  };

  return (
    <Box
      className={styles.listGroupItem}
      border="1px"
      borderColor="gray.200"
      bg="white"
      padding="4"
    >
      <HStack paddingBottom="0" spacing="4" alignItems="flex-start">
        {menu.image?.url && menu.name && (
          <Image src={menu.image.url} boxSize="16" alt={menu.name} objectFit="cover" />
        )}
        <VStack alignItems="flex-start">
          <Box fontWeight="bold">
            {menu.name}({menu.time_required}分)
          </Box>
          <Text fontWeight="bold">
            {typeof menu.price === 'number' && `¥${numberFormat(Number(menu.price))}`}
          </Text>
        </VStack>
      </HStack>
      {menu.description && (
        <Box mt={2}>
          {isCutDescription() && (
            <>
              {isShowShortDescription && (
                <>
                  <WrappedText>
                    {menu.description.substring(0, maxLength)}
                    ...
                  </WrappedText>
                  <CustomButton
                    variant="link"
                    fontSize="1em"
                    fontWeight="normal"
                    textDecoration="underline"
                    onClick={handleToggleShortDescription}
                  >
                    すべて表示
                  </CustomButton>
                </>
              )}
              {!isShowShortDescription && <WrappedText>{menu.description}</WrappedText>}
            </>
          )}
          {!isCutDescription() && <WrappedText>{menu.description}</WrappedText>}
        </Box>
      )}
      <VStack alignItems="flex-end">
        <CustomButton variant="solid" onClick={() => router.push(handleNextLink(Number(menu.id)))}>
          選択する
        </CustomButton>
      </VStack>
    </Box>
  );
};

const ReservationPageTop: LayoutNextPage<ServerProps> = function ReservationPageTop(
  props: ServerProps
) {
  const { result, rvPage } = props;

  return (
    <Stack fontSize={14}>
      {rvPage.reservationPage?.coverImage?.url && (
        <Box bg="white" mb="4">
          <HStack justifyContent="center">
            <Image
              src={rvPage.reservationPage?.coverImage?.url}
              alt="COVER IMAGE"
              objectFit="cover"
              maxWidth="640"
              maxHeight="600"
              width="100%"
            />
          </HStack>
        </Box>
      )}

      <Box
        backgroundColor="white"
        className={styles.listGroupItem}
        border="1px"
        borderColor="gray.200"
        paddingX="4"
        paddingY="4"
      >
        <WrappedText>{rvPage?.reservationPage?.description}</WrappedText>
      </Box>
      {result.menus?.data.map(
        (menu) => menu.is_published && <MenuComponent key={menu.id} menu={menu} props={props} />
      )}
    </Stack>
  );
};

ReservationPageTop.getLayout = (page: ReactElement<ServerProps>) => {
  return (
    <UserPageLayout contentPadding="0" pageNameProp={page.props.rvPage.reservationPage?.name || ''}>
      {page}
    </UserPageLayout>
  );
};

export const getServerSideProps: GetServerSideProps<ServerProps> = async (ctx) => {
  const liffRedirect = liffSsrStateRedirect(ctx);
  if (liffRedirect) {
    return {
      redirect: liffRedirect,
    };
  }

  const client = ApolloClientManager.getFromSSR('api', ctx);

  const { reservationCode } = ctx.query;

  try {
    // reservation page
    const reservationPage = await client.query<
      GetReservationPageCommonQuery,
      GetReservationPageCommonQueryVariables
    >({
      query: GetReservationPageCommonDocument,
      variables: {
        rvPageCode: String(reservationCode),
      },
    });

    if (!reservationPage.data.reservationPage?.id) {
      return {
        notFound: true,
      };
    }

    const result = await client.query<
      GetReservationPageIndexPageQuery,
      GetReservationPageIndexPageQueryVariables
    >({
      query: GetReservationPageIndexPageDocument,
      variables: {
        rvPageId: reservationPage.data.reservationPage?.id,
      },
    });

    const rvPage = reservationPage.data;

    return {
      props: {
        result: result.data,
        rvPage,
      },
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
};

export default ReservationPageTop;
